import React, { useEffect, useRef, useState } from "react";
import { engine } from "./engine";

export function useEngineFrame(fn) {
  const ref = useRef(fn); ref.current = fn;
  useEffect(() => {
    let id;
    const loop = () => { ref.current(); id = requestAnimationFrame(loop); };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, []);
}

export function usePlaying() {
  const [p, setP] = useState(engine.playing);
  useEffect(() => { const l = (v) => setP(v); engine.listeners.add(l); return () => engine.listeners.delete(l); }, []);
  return p;
}

export function Knob({ value, min = -1, max = 1, onChange, size = 30, label, color = "#fbbf24", def = 0, testid, fmt }) {
  const start = useRef(null);
  const norm = (value - min) / (max - min);
  const ang = -135 + norm * 270;
  const down = (e) => {
    e.preventDefault();
    start.current = { y: e.clientY, v: value };
    const move = (ev) => { const d = (start.current.y - ev.clientY) / 150; onChange(Math.min(max, Math.max(min, start.current.v + d * (max - min)))); };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  const r = size / 2 - 3, c = size / 2;
  const arc = (a0, a1) => { const p = (a) => [c + r * Math.cos(((a - 90) * Math.PI) / 180), c + r * Math.sin(((a - 90) * Math.PI) / 180)]; const [x0, y0] = p(a0), [x1, y1] = p(a1); return `M${x0} ${y0} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1} ${y1}`; };
  const zero = min < 0 ? 0 : -135;
  return (
    <div className="flex flex-col items-center select-none" title={fmt ? fmt(value) : value.toFixed(2)}>
      <svg width={size} height={size} className="knob" onPointerDown={down} onDoubleClick={() => onChange(def)} data-testid={testid}>
        <path d={arc(-135, 135)} stroke="#15171a" strokeWidth="3" fill="none" strokeLinecap="round" />
        {Math.abs(ang - zero) > 1 && <path d={ang > zero ? arc(zero, ang) : arc(ang, zero)} stroke={color} strokeWidth="3" fill="none" strokeLinecap="round" />}
        <circle cx={c} cy={c} r={r - 4} fill="#3a3d44" stroke="#15171a" />
        <line x1={c} y1={c} x2={c + (r - 5) * Math.cos(((ang - 90) * Math.PI) / 180)} y2={c + (r - 5) * Math.sin(((ang - 90) * Math.PI) / 180)} stroke="#e5e7eb" strokeWidth="2" strokeLinecap="round" />
      </svg>
      {label && <span className="text-[9px] text-zinc-500 mt-0.5 uppercase tracking-wide font-mono">{label}</span>}
    </div>
  );
}

export function Fader({ value, onChange, height = 150, testid }) {
  const ref = useRef(null);
  const set = (clientY) => { const r = ref.current.getBoundingClientRect(); onChange(Math.min(1, Math.max(0, 1 - (clientY - r.top) / r.height))); };
  const down = (e) => {
    e.preventDefault(); set(e.clientY);
    const move = (ev) => set(ev.clientY);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return (
    <div ref={ref} onPointerDown={down} onDoubleClick={() => onChange(0.78)} data-testid={testid} className="relative w-7 cursor-ns-resize" style={{ height }}>
      <div className="absolute left-1/2 -translate-x-1/2 w-1.5 h-full rounded fader-track border border-black" />
      {[1, 0.78, 0.5, 0.25, 0].map((m) => <div key={m} className="absolute left-0 w-1.5 h-px bg-zinc-600" style={{ top: (1 - m) * height }} />)}
      <div className="absolute left-1/2 -translate-x-1/2 w-7 h-4 rounded-sm border border-black shadow-md" style={{ top: (1 - value) * height - 8, background: "linear-gradient(#cfd2d8,#8b8f97 45%,#5e626a 55%,#9ea2aa)" }}>
        <div className="absolute top-1/2 inset-x-1 h-px bg-black/70" />
      </div>
    </div>
  );
}

const toDb = (v) => (v <= 0.00001 ? -Infinity : 20 * Math.log10(v));
export function Meter({ trackId, master, height = 150, width = 6, stereo = true }) {
  const bars = useRef([]);
  const peak = useRef(0), hold = useRef(0);
  useEngineFrame(() => {
    const lv = master ? engine.masterLevel() : engine.trackLevel(trackId);
    peak.current = Math.max(lv, peak.current * 0.92);
    const db = toDb(peak.current), pct = Math.max(0, Math.min(1, (db + 60) / 60));
    hold.current = Math.max(pct, hold.current - 0.006);
    bars.current.forEach((b, i) => { if (b) { b.style.height = (pct * (i ? 0.96 : 1)) * 100 + "%"; } });
  });
  return (
    <div className="flex gap-[2px] bg-black/80 p-[2px] rounded-sm border border-black" style={{ height }}>
      {(stereo ? [0, 1] : [0]).map((i) => (
        <div key={i} className="relative bg-[#101113]" style={{ width }}>
          <div ref={(el) => (bars.current[i] = el)} className="absolute bottom-0 inset-x-0" style={{ height: 0, background: "linear-gradient(to top,#22c55e 0%,#84cc16 60%,#facc15 80%,#ef4444 96%)", backgroundSize: `100% ${height}px`, backgroundPosition: "bottom" }} />
        </div>
      ))}
    </div>
  );
}

export function MiniMeter({ trackId }) {
  const el = useRef(null), pk = useRef(0);
  useEngineFrame(() => { const lv = engine.trackLevel(trackId); pk.current = Math.max(lv, pk.current * 0.9); if (el.current) el.current.style.width = Math.max(0, Math.min(1, (toDb(pk.current) + 50) / 50)) * 100 + "%"; });
  return <div className="h-1 w-full bg-black/60 rounded overflow-hidden"><div ref={el} className="h-full" style={{ background: "linear-gradient(90deg,#22c55e,#facc15 80%,#ef4444)" }} /></div>;
}

export const dbLabel = (v) => { const d = toDb(v * v); return d === -Infinity ? "-∞" : d.toFixed(1); };
